import React, { useEffect, useMemo, useState } from 'react';
import BaseModal from './BaseModal';
import { INPUT, LABEL, ACTION_BTN } from './ui';
import {
  ADMIN_GROUP_LABEL,
  creatableRoles,
  isOneTime,
  MAX_ONE_TIME_ACCOUNTS,
  ONE_TIME_ROLE,
  ROLE_LABELS,
  supervisorOptions as managersIn,
} from './roles';
import { isValidEmail, parseEmails } from './emailParsing';

// Themed modal for creating users. The account type is asked first, because it
// decides what the rest of the form even is: a one-time account is created
// against a survey and usually arrives as a batch of addresses, while every
// other role is a single person with a place in the hierarchy.
//
// Supervisor and company fields adapt to the actor's role:
//  - dev: pick any company (dropdown) + optional manager from that company
//  - admin: company locked to own; optional manager dropdown
//  - manager: company locked; supervisor is implicitly the manager (hidden)
//
// The supervisor is always optional, and only managers appear in it. Leaving it
// blank is the ordinary case for a small team: the account lands under the
// company's admins, who supervise as a group.
const CreateUserModal = ({
  open,
  actor,
  companies = [],
  users = [],
  forms = [],
  defaultCompanyId = null,
  onConfirm,
  onCancel,
}) => {
  const roleOptions = useMemo(() => creatableRoles(actor?.role), [actor]);
  const isDev = actor?.role === 'dev';
  const isManager = actor?.role === 'manager';

  const [role, setRole] = useState('inspector');
  const [email, setEmail] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [managerId, setManagerId] = useState('');
  const [formId, setFormId] = useState('');
  // One-time only: the addresses staged for creation, the paste box, and the
  // note explaining what the last add did or didn't take.
  const [emails, setEmails] = useState([]);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [notice, setNotice] = useState('');

  const oneTime = isOneTime(role);

  const resetFields = () => {
    setEmail('');
    setEmails([]);
    setPasteOpen(false);
    setPasteText('');
    setNotice('');
    setFormId('');
    // Blank means "the admins", which is the right default for every role. For
    // a one-time batch the API turns that into whoever actually keeps the
    // results, so there is nothing to preselect here.
    setManagerId('');
  };

  useEffect(() => {
    if (!open) return;
    const initialRole = roleOptions.includes('inspector') ? 'inspector' : roleOptions[0] || '';
    setRole(initialRole);
    const initialCompany = isDev
      ? (defaultCompanyId || companies[0]?.id || '')
      : actor?.company_id || '';
    setCompanyId(initialCompany);
    resetFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isDev, defaultCompanyId, companies, actor, roleOptions]);

  const effectiveCompanyId = isDev ? Number(companyId) : actor?.company_id;

  // Only managers. An admin account is not a supervisor on its own any more —
  // the blank option below is how you choose the admins.
  const supervisorOptions = useMemo(
    () => managersIn(users, effectiveCompanyId),
    [users, effectiveCompanyId],
  );

  const surveyOptions = useMemo(
    () => forms.filter((f) => f.company_id === effectiveCompanyId),
    [forms, effectiveCompanyId],
  );

  const companyHasAdmin = useMemo(
    () => users.some((u) => u.role === 'admin' && u.company_id === effectiveCompanyId),
    [users, effectiveCompanyId],
  );

  const showSupervisor = (role === 'inspector' || oneTime) && !isManager;

  const lockedCompanyName =
    actor?.company_name || companies.find((c) => c.id === actor?.company_id)?.name || '—';

  const handleRoleChange = (nextRole) => {
    setRole(nextRole);
    resetFields();
  };

  // Adds addresses to the staged list, reporting what it left out. Everything
  // that can go wrong with a paste — duplicates, an address that already has an
  // account, more than fifty — is worth saying out loud rather than silently
  // dropping, because the whole point of the list is that it can be checked.
  const addEmails = (candidates, { unparsed = 0 } = {}) => {
    const existing = new Set(emails);
    const accepted = [];
    let duplicates = 0;
    candidates.forEach((candidate) => {
      if (existing.has(candidate)) {
        duplicates += 1;
        return;
      }
      existing.add(candidate);
      accepted.push(candidate);
    });

    const room = MAX_ONE_TIME_ACCOUNTS - emails.length;
    const taken = accepted.slice(0, Math.max(room, 0));
    const overflow = accepted.length - taken.length;
    if (taken.length) setEmails((prev) => [...prev, ...taken]);

    const notes = [];
    if (taken.length) notes.push(`Added ${taken.length}`);
    if (duplicates) notes.push(`${duplicates} already in the list`);
    if (unparsed) notes.push(`${unparsed} not recognized as an address`);
    if (overflow) {
      notes.push(`${overflow} over the limit of ${MAX_ONE_TIME_ACCOUNTS}`);
    }
    setNotice(notes.join(' · '));
    return taken.length;
  };

  const handleAddTyped = () => {
    const candidate = email.trim().toLowerCase();
    if (!candidate) return;
    if (!isValidEmail(candidate)) {
      setNotice(`${candidate} is not a valid email address`);
      return;
    }
    if (addEmails([candidate])) setEmail('');
  };

  const handleAddPasted = () => {
    const parsed = parseEmails(pasteText);
    if (!parsed.length) {
      setNotice('No email addresses found in that text');
      return;
    }
    addEmails(parsed);
    setPasteText('');
  };

  const removeEmail = (target) => {
    setEmails((prev) => prev.filter((e) => e !== target));
    setNotice('');
  };

  const parsedPreview = useMemo(() => parseEmails(pasteText).length, [pasteText]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (oneTime) {
      if (!emails.length || !formId) return;
      const payload = {
        role: ONE_TIME_ROLE,
        emails,
        form_id: Number(formId),
      };
      if (isDev) payload.company_id = Number(companyId) || null;
      if (showSupervisor) payload.manager_id = Number(managerId) || null;
      onConfirm(payload);
      return;
    }

    // Addresses are stored lowercase so sign-in never depends on the
    // capitalization whoever created the account happened to type.
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return;
    const payload = { email: trimmed, role };
    if (isDev) payload.company_id = Number(companyId) || null;
    if (showSupervisor) payload.manager_id = Number(managerId) || null;
    onConfirm(payload);
  };

  // What blank vs. a named manager actually does, which differs by account
  // type: for a lasting account it is a reporting line and a survey catalog,
  // for a one-time account it is who keeps the answers once it is gone.
  let supervisorHint;
  if (oneTime) {
    supervisorHint = managerId
      ? 'Once a one-time account is used it is deleted, and its submission is kept under this manager.'
      : `Once a one-time account is used it is deleted, and its submission is kept by the ${ADMIN_GROUP_LABEL.toLowerCase()}.`;
  } else {
    supervisorHint = managerId
      ? "They join this manager's team and work from that manager's surveys."
      : `They report to the ${ADMIN_GROUP_LABEL.toLowerCase()} and work from the surveys the admins create.`;
  }

  // The count on the button is the last chance to notice that the list holds
  // more (or fewer) people than expected.
  let confirmLabel = 'Add user';
  if (oneTime) {
    confirmLabel = emails.length
      ? `Create ${emails.length} account${emails.length === 1 ? '' : 's'}`
      : 'Create accounts';
  }

  return (
    <BaseModal
      open={open}
      titleId="create-user-title"
      title={oneTime ? 'Add one-time users' : 'Add user'}
      message={
        oneTime
          ? 'Each account can open one survey once. It is deleted as soon as it is submitted; the answers stay with the supervisor.'
          : "They'll receive a one-time password you can share; they must set their own at first login."
      }
      onSubmit={handleSubmit}
      onCancel={onCancel}
      confirmLabel={confirmLabel}
      widthClass={oneTime ? 'max-w-2xl' : 'max-w-md'}
      bodyClassName="px-6 py-4 space-y-4 max-h-[65vh] overflow-y-auto"
    >
      <div>
        <label htmlFor="cu-role" className={LABEL}>Account type</label>
        <select
          id="cu-role"
          value={role}
          onChange={(e) => handleRoleChange(e.target.value)}
          className={INPUT}
        >
          {roleOptions.map((r) => (
            <option key={r} value={r}>{ROLE_LABELS[r]}</option>
          ))}
        </select>
      </div>

      {oneTime ? (
        <div>
          <div className="flex items-baseline justify-between gap-3 mb-2">
            <label htmlFor="cu-email" className={`${LABEL} mb-0`}>Emails</label>
            <span className="text-xs text-gray-400">
              {emails.length} of {MAX_ONE_TIME_ACCOUNTS}
            </span>
          </div>

          <div className="flex gap-2">
            <input
              id="cu-email"
              type="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => {
                // Enter adds to the list rather than submitting a form that
                // isn't finished yet.
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddTyped();
                }
              }}
              placeholder="name@example.com"
              className={INPUT}
            />
            <button
              type="button"
              onClick={handleAddTyped}
              disabled={!email.trim() || emails.length >= MAX_ONE_TIME_ACCOUNTS}
              className={`${ACTION_BTN} px-4 whitespace-nowrap`}
            >
              Add
            </button>
          </div>

          <label className="mt-3 flex items-center gap-2 text-sm text-gray-300">
            <input
              type="checkbox"
              checked={pasteOpen}
              onChange={(e) => {
                setPasteOpen(e.target.checked);
                if (!e.target.checked) setPasteText('');
              }}
              className="w-4 h-4 accent-purple-500"
            />
            Paste a list of emails
          </label>

          {pasteOpen && (
            <div className="mt-2">
              <textarea
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                rows={7}
                spellCheck={false}
                placeholder={
                  'Paste up to 50 addresses — separated by commas, spaces, or line breaks, ' +
                  'with or without names beside them.'
                }
                className={`${INPUT} font-mono text-sm resize-y`}
              />
              <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
                <span className="text-xs text-gray-400">
                  {pasteText.trim()
                    ? `${parsedPreview} address${parsedPreview === 1 ? '' : 'es'} found`
                    : 'Names, quotes and angle brackets around the addresses are fine.'}
                </span>
                <button
                  type="button"
                  onClick={handleAddPasted}
                  disabled={!parsedPreview || emails.length >= MAX_ONE_TIME_ACCOUNTS}
                  className={ACTION_BTN}
                >
                  Add to list
                </button>
              </div>
            </div>
          )}

          {notice && <p className="mt-2 text-xs text-amber-300">{notice}</p>}

          {emails.length > 0 && (
            <ul className="mt-3 max-h-56 overflow-y-auto rounded-lg border border-gray-600 divide-y divide-gray-700">
              {emails.map((entry, index) => (
                <li
                  key={entry}
                  className="flex items-center justify-between gap-3 px-3 py-2 bg-gray-700/30"
                >
                  <span className="flex items-center gap-3 min-w-0">
                    <span className="text-xs text-gray-500 w-6 flex-shrink-0 text-right">
                      {index + 1}
                    </span>
                    <span className="text-sm text-white truncate">{entry}</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => removeEmail(entry)}
                    aria-label={`Remove ${entry}`}
                    className="bg-transparent hover:bg-red-500/20 text-gray-400 hover:text-red-300 px-2 py-1 rounded text-sm leading-none flex-shrink-0"
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}

          {emails.length === 0 && (
            <p className="mt-3 text-xs text-gray-500">
              Add the addresses one at a time, or paste them in — they are listed here to check
              before any accounts are created.
            </p>
          )}
        </div>
      ) : (
        <div>
          <label htmlFor="cu-email" className={LABEL}>Email</label>
          <input
            id="cu-email"
            type="email"
            required
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            inputMode="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@example.com"
            className={INPUT}
          />
        </div>
      )}

      <div>
        <label htmlFor="cu-company" className={LABEL}>Company</label>
        {isDev ? (
          <select
            id="cu-company"
            value={companyId}
            onChange={(e) => {
              setCompanyId(e.target.value);
              setManagerId('');
              setFormId('');
            }}
            className={INPUT}
            required
          >
            <option value="" disabled>Select a company</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        ) : (
          <input
            id="cu-company"
            type="text"
            value={lockedCompanyName}
            disabled
            className={`${INPUT} opacity-60 cursor-not-allowed`}
          />
        )}
      </div>

      {oneTime && (
        <div>
          <label htmlFor="cu-survey" className={LABEL}>Survey</label>
          <select
            id="cu-survey"
            value={formId}
            onChange={(e) => setFormId(e.target.value)}
            className={INPUT}
            required
          >
            <option value="" disabled>Select the survey these accounts open</option>
            {surveyOptions.map((f) => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </select>
          {surveyOptions.length === 0 && (
            <p className="mt-2 text-xs text-amber-300">
              No surveys available here yet — create one on the Forms tab first.
            </p>
          )}
        </div>
      )}

      {showSupervisor && (
        <div>
          <label htmlFor="cu-supervisor" className={LABEL}>
            Manager <span className="font-normal text-gray-500">(optional)</span>
          </label>
          <select
            id="cu-supervisor"
            value={managerId}
            onChange={(e) => setManagerId(e.target.value)}
            className={INPUT}
          >
            {/* Not a "none" option: it names who the account actually reports
                to, which is why it reads as a choice rather than a blank. */}
            <option value="">{ADMIN_GROUP_LABEL} — no manager</option>
            {supervisorOptions.map((u) => (
              <option key={u.id} value={u.id}>{u.email}</option>
            ))}
          </select>
          <p className="mt-2 text-xs text-gray-500">{supervisorHint}</p>
          {oneTime && !managerId && !companyHasAdmin && (
            <p className="mt-2 text-xs text-amber-300">
              This company has no admin to keep the results — pick a manager, or add an admin
              first.
            </p>
          )}
        </div>
      )}
    </BaseModal>
  );
};

export default CreateUserModal;
